// Sound system: maps logical game events to files under `snd/` and plays them.
//
// The file list is fetched live from the map-server's `/sounds` endpoint, so the
// user can drop new files into `snd/` and hit "Reload" in the Sound menu tab.
// Assignment (event -> file) lives in Settings and is persisted with the rest.
//
// Playback uses the Web Audio API: each one-shot is a fresh `AudioBufferSource`
// fed from a decoded+cached `AudioBuffer`. That avoids the `<audio>` element
// state/autoplay pitfalls and makes overlapping sounds (footsteps + jump)
// reliable. The `AudioContext` is created/resumed on the first user gesture.

import { api } from "./base";
import {
  EV_DASH,
  EV_DEATH,
  EV_EXPLODE,
  EV_FOOTSTEP,
  EV_HIT,
  EV_JUMP,
  EV_JUMPPAD,
  EV_LAND,
  EV_PAIN,
  EV_PICKUP,
  EV_TELEPORT,
  EV_WALLJUMP,
} from "./net/events";

export interface SoundEventDef {
  id: string;
  label: string;
}

/**
 * Every assignable sound effect. The first block is emitted by the current sim;
 * the weapon/pickup ones are placeholders until those systems land, but stay
 * configurable so files can be auditioned now.
 */
export const SOUND_EVENTS: SoundEventDef[] = [
  { id: "jump", label: "Jump" },
  { id: "dash", label: "Dash" },
  { id: "walljump", label: "Wall dash / walljump" },
  { id: "land", label: "Land" },
  { id: "footstep", label: "Footstep" },
  { id: "jumppad", label: "Jump pad" },
  { id: "teleport", label: "Teleport" },
  { id: "pickup", label: "Item pickup" },
  { id: "rocket_fire", label: "Rocket — fire" },
  { id: "rocket_explode", label: "Explosion" },
  { id: "plasma_fire", label: "Plasma — fire" },
  { id: "plasma_explode", label: "Plasma — explode" },
  { id: "grenade_fire", label: "Grenade — fire" },
  { id: "grenade_explode", label: "Grenade — explode" },
  { id: "lightning_fire", label: "Lightning Gun — fire" },
  { id: "rail_fire", label: "Railgun — fire" },
  { id: "machinegun_fire", label: "Machinegun — fire" },
  { id: "shotgun_fire", label: "Shotgun — fire" },
  { id: "melee_fire", label: "Melee — fire" },
  { id: "experimental_fire", label: "Experimental — fire" },
  { id: "hit", label: "Hit" },
  { id: "pain", label: "Pain" },
  { id: "death", label: "Death" },
  { id: "checkpoint", label: "Checkpoint" },
  { id: "finish", label: "Finish" },
  { id: "ping", label: "Ping / tag" },
];

/**
 * Gameplay event bits -> sound event ids. The bits come from the generated
 * `net/events.ts` (single source: `core/src/wire.rs` `event_schema()`).
 */
const EVENT_BITS: Array<[string, number]> = [
  ["jump", EV_JUMP],
  ["dash", EV_DASH],
  ["walljump", EV_WALLJUMP],
  ["land", EV_LAND],
  ["footstep", EV_FOOTSTEP],
  ["jumppad", EV_JUMPPAD],
  ["teleport", EV_TELEPORT],
  ["pickup", EV_PICKUP],
  // Combat (EV_FIRE is weapon-specific and handled in main.ts).
  ["rocket_explode", EV_EXPLODE],
  ["hit", EV_HIT],
  ["pain", EV_PAIN],
  ["death", EV_DEATH],
];

export class SoundManager {
  private assignments: Record<string, string> = {};
  private volume = 1.0;
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  /** Per-weapon hit-sound toggle (weapon profile). */
  private hitSoundsEnabled = true;
  /** Extra gain for the "hit" event (Settings → Video → hit feedback). */
  private hitVolume = 1;
  /** Multiplier for the synthesized placeholder currently being played. */
  private eventGain = 1;
  /** Decoded buffers, cached by file name. */
  private buffers = new Map<string, AudioBuffer>();
  /** In-flight decodes (so repeated plays don't fetch twice). */
  private pending = new Map<string, Promise<AudioBuffer | null>>();
  private failed = new Set<string>();

  setAssignments(a: Record<string, string>) {
    this.assignments = { ...a };
  }
  getAssignments(): Record<string, string> {
    return { ...this.assignments };
  }
  setHitSoundsEnabled(v: boolean) {
    this.hitSoundsEnabled = v;
  }

  setHitVolume(v: number) {
    this.hitVolume = Math.min(2, Math.max(0, v));
  }

  setVolume(v: number) {
    this.volume = Math.min(1, Math.max(0, v));
    if (this.master) this.master.gain.value = this.volume;
  }

  /** Fetch the current list of playable files under `snd/`. */
  async listFiles(): Promise<string[]> {
    try {
      const res = await fetch(api("/sounds"), { cache: "no-store" });
      if (!res.ok) return [];
      const json = (await res.json()) as { files?: unknown };
      return Array.isArray(json.files) ? (json.files as string[]) : [];
    } catch {
      return [];
    }
  }

  /**
   * Create/resume the `AudioContext`. Call this from a user gesture (pointer or
   * key) so browsers allow audio to start.
   */
  unlock() {
    const ctx = this.ensureCtx();
    if (ctx && ctx.state === "suspended") void ctx.resume();
  }

  private ensureCtx(): AudioContext | null {
    if (this.ctx) {
      if (this.ctx.state === "suspended") void this.ctx.resume();
      return this.ctx;
    }
    const Ctor =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return null;
    try {
      this.ctx = new Ctor();
      this.master = this.ctx.createGain();
      this.master.gain.value = this.volume;
      this.master.connect(this.ctx.destination);
    } catch (e) {
      console.warn("[sound] AudioContext unavailable:", (e as Error).message);
      this.ctx = null;
    }
    return this.ctx;
  }

  /**
   * Play the sound assigned to a logical event; if none is set (or its file is
   * missing), fall back to a synthesized **placeholder** so there is always
   * feedback without shipping weapon assets.
   */
  play(eventId: string, gain = 1) {
    const file = this.assignments[eventId];
    if (file && !this.failed.has(file)) {
      this.playFile(file, eventId, gain);
      return;
    }
    this.playProcedural(eventId, gain);
  }

  /** Play a specific `snd/` file directly (menu preview + gameplay). */
  playFile(file: string, fallbackEvent?: string, gain = 1) {
    if (!file || this.failed.has(file)) {
      if (fallbackEvent) this.playProcedural(fallbackEvent, gain);
      return;
    }
    const ctx = this.ensureCtx();
    if (!ctx || !this.master) return;
    const cached = this.buffers.get(file);
    if (cached) {
      this.start(ctx, cached, gain);
      return;
    }
    void this.loadBuffer(ctx, file).then((buf) => {
      if (buf) this.start(ctx, buf, gain);
      else if (fallbackEvent) this.playProcedural(fallbackEvent, gain);
    });
  }

  // ---- Synthesized placeholder sounds (no assets required) ----------------

  private noiseBuffer: AudioBuffer | null = null;

  private noise(ctx: AudioContext): AudioBuffer {
    if (this.noiseBuffer) return this.noiseBuffer;
    const len = Math.max(1, Math.floor(ctx.sampleRate * 0.5));
    const buf = ctx.createBuffer(1, len, ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
    this.noiseBuffer = buf;
    return buf;
  }

  private tone(
    ctx: AudioContext,
    f0: number,
    f1: number,
    dur: number,
    type: OscillatorType,
    gain: number,
  ) {
    const t = ctx.currentTime;
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = type;
    o.frequency.setValueAtTime(f0, t);
    o.frequency.exponentialRampToValueAtTime(Math.max(1, f1), t + dur);
    g.gain.setValueAtTime(gain * this.eventGain, t);
    g.gain.exponentialRampToValueAtTime(0.0008, t + dur);
    o.connect(g);
    g.connect(this.master!);
    o.start(t);
    o.stop(t + dur + 0.02);
  }

  private noiseBurst(ctx: AudioContext, dur: number, gain: number, cutoff: number) {
    const t = ctx.currentTime;
    const src = ctx.createBufferSource();
    src.buffer = this.noise(ctx);
    const filter = ctx.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.setValueAtTime(cutoff, t);
    filter.frequency.exponentialRampToValueAtTime(Math.max(60, cutoff * 0.2), t + dur);
    const g = ctx.createGain();
    g.gain.setValueAtTime(gain * this.eventGain, t);
    g.gain.exponentialRampToValueAtTime(0.0008, t + dur);
    src.connect(filter);
    filter.connect(g);
    g.connect(this.master!);
    src.start(t);
    src.stop(t + dur + 0.02);
  }

  /** A synthesized placeholder for an event id (used when no file is set). */
  private playProcedural(eventId: string, gain = 1) {
    const ctx = this.ensureCtx();
    if (!ctx || !this.master) return;
    this.eventGain = gain;
    switch (eventId) {
      // Movement
      case "jump": this.tone(ctx, 320, 520, 0.08, "square", 0.18); break;
      case "dash": this.noiseBurst(ctx, 0.2, 0.35, 1400); break;
      case "walljump": this.tone(ctx, 520, 820, 0.1, "sawtooth", 0.22); break;
      case "land": this.tone(ctx, 220, 90, 0.1, "triangle", 0.28); break;
      case "footstep": this.noiseBurst(ctx, 0.05, 0.12, 900); break;
      case "jumppad": this.tone(ctx, 300, 1300, 0.25, "square", 0.28); break;
      case "teleport": this.tone(ctx, 1400, 300, 0.3, "sawtooth", 0.28); break;
      case "pickup":
        // Q3-style two-note pickup blip.
        this.tone(ctx, 880, 880, 0.06, "square", 0.25);
        this.tone(ctx, 1320, 1320, 0.09, "square", 0.22);
        break;
      // Weapons — fire
      case "rocket_fire": this.tone(ctx, 240, 80, 0.2, "sawtooth", 0.5); this.noiseBurst(ctx, 0.12, 0.4, 1600); break;
      case "grenade_fire": this.tone(ctx, 170, 70, 0.22, "triangle", 0.5); break;
      case "plasma_fire": this.tone(ctx, 900, 520, 0.08, "square", 0.28); break;
      case "lightning_fire": this.tone(ctx, 1500, 1300, 0.05, "sawtooth", 0.22); break;
      case "rail_fire": this.tone(ctx, 1900, 320, 0.26, "sawtooth", 0.38); break;
      case "machinegun_fire": this.tone(ctx, 520, 320, 0.05, "square", 0.22); break;
      case "shotgun_fire": this.noiseBurst(ctx, 0.18, 0.6, 1800); break;
      case "melee_fire": this.tone(ctx, 320, 160, 0.12, "triangle", 0.35); break;
      case "experimental_fire":
        this.tone(ctx, 700, 1400, 0.14, "sawtooth", 0.4);
        this.tone(ctx, 1400, 300, 0.2, "square", 0.25);
        break;
      // Explosions / impacts
      case "rocket_explode":
      case "grenade_explode":
      case "plasma_explode":
        this.noiseBurst(ctx, 0.45, 0.9, 900);
        this.tone(ctx, 140, 50, 0.4, "sawtooth", 0.4);
        break;
      case "hit": this.tone(ctx, 1300, 950, 0.05, "square", 0.28); break;
      case "pain": this.tone(ctx, 320, 200, 0.12, "sawtooth", 0.35); break;
      case "death": this.tone(ctx, 420, 60, 0.6, "sawtooth", 0.5); break;
      default:
        break;
    }
    this.eventGain = 1;
  }

  private start(ctx: AudioContext, buf: AudioBuffer, gain = 1) {
    try {
      const src = ctx.createBufferSource();
      src.buffer = buf;
      if (gain !== 1) {
        const g = ctx.createGain();
        g.gain.value = gain;
        src.connect(g);
        g.connect(this.master!);
      } else {
        src.connect(this.master!);
      }
      src.start();
    } catch (e) {
      console.warn("[sound] play failed:", (e as Error).message);
    }
  }

  private loadBuffer(ctx: AudioContext, file: string): Promise<AudioBuffer | null> {
    const existing = this.pending.get(file);
    if (existing) return existing;
    const p = (async () => {
      try {
        const res = await fetch(api(`/snd/${encodeURIComponent(file)}`), { cache: "no-store" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const bytes = await res.arrayBuffer();
        const buf = await ctx.decodeAudioData(bytes);
        this.buffers.set(file, buf);
        return buf;
      } catch (e) {
        this.failed.add(file);
        console.warn(`[sound] failed to load "${file}":`, (e as Error).message);
        return null;
      } finally {
        this.pending.delete(file);
      }
    })();
    this.pending.set(file, p);
    return p;
  }

  /** Play all sounds whose event bit is set in `bits`. */
  handleBits(bits: number) {
    if (!bits) return;
    for (const [id, bit] of EVENT_BITS) {
      if (id === "hit" && !this.hitSoundsEnabled) continue;
      if (bits & bit) this.play(id, id === "hit" ? this.hitVolume : 1);
    }
  }
}
